import React, { useState } from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import { createUseStyles } from 'react-jss';
import { Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { NiasLogoIcon, WPLogoIcon, SynthesiaLogoIcon } from './ActiveIntegrations';
import { isPlatform, Platform } from './AddNewIntegration';

interface LMSPlatformSelectorProps {
  value?: string | null | undefined;
  onChange?: (value: Platform) => void;
}

export function LMSPlatformSelector({ value, onChange }: LMSPlatformSelectorProps) {
  const theme = useTheme();
  const [selectedValue, setSelectedValue] = useState(value || 'wordpress');

  const useStyles = createUseStyles((theme: Theme) => ({
    formContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'row',
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
      },
    },

    radioItem: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      cursor: 'pointer',
      width: 300,
      boxSizing: 'border-box',
      userSelect: 'none',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    selected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + '20',
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 48,
      height: 48,
      marginRight: theme.spacing(2),
      flexShrink: 0,
      '& svg': {
        width: 32,
        height: 32,
      },
      '& img': {
        height: 32,
        width: 'auto',
        maxHeight: 32,
      },
    },
    platformInfo: {
      flex: 1,
      minWidth: 0,
    },
    platformName: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    platformDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
  }));
  const classes = useStyles({ theme });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    if (isPlatform(newValue)) onChange?.(newValue);
  };

  return (
    <div className={classes.formContainer}>
      <Typography gutterBottom>Select Integration Platform</Typography>
      <RadioGroup className={classes.radioGroup} value={selectedValue} onChange={handleChange}>
        <FormControlLabel
          value='wordpress'
          control={<Radio style={{ display: 'none' }} />}
          label={
            <div className={`${classes.radioItem} ${selectedValue === 'wordpress' ? classes.selected : ''}`}>
              <div className={classes.iconContainer}>
                <WPLogoIcon />
              </div>
              <div className={classes.platformInfo}>
                <Typography className={classes.platformName}>WordPress</Typography>
                <Typography className={classes.platformDescription}>Popular CMS with LMS plugins</Typography>
              </div>
            </div>
          }
          labelPlacement='end'
        />
        <FormControlLabel
          value='mentorkitLMS'
          control={<Radio style={{ display: 'none' }} />}
          label={
            <div className={`${classes.radioItem} ${selectedValue === 'mentorkitLMS' ? classes.selected : ''}`}>
              <div className={classes.iconContainer}>
                <NiasLogoIcon />
              </div>
              <div className={classes.platformInfo}>
                <Typography className={classes.platformName}>Mentorkit Enterprise LMS</Typography>
                <Typography className={classes.platformDescription}>LMS by Norsk Interaktiv</Typography>
              </div>
            </div>
          }
          labelPlacement='end'
        />
        <FormControlLabel
          value='synthesia'
          control={<Radio style={{ display: 'none' }} />}
          label={
            <div className={`${classes.radioItem} ${selectedValue === 'synthesia' ? classes.selected : ''}`}>
              <div className={classes.iconContainer}>
                <SynthesiaLogoIcon />
              </div>
              <div className={classes.platformInfo}>
                <Typography className={classes.platformName}>Synthesia</Typography>
                <Typography className={classes.platformDescription}>AI video creation platform</Typography>
              </div>
            </div>
          }
          labelPlacement='end'
        />
      </RadioGroup>
    </div>
  );
}
